/**
 * 入力検証ユーティリティ
 * フロント/バック双方で利用する基本バリデーションを提供します
 */

/** ニックネーム最小文字数 */
export const NICKNAME_MIN_LENGTH = 1;

/** ニックネーム最大文字数 */
export const NICKNAME_MAX_LENGTH = 20;

/** 来場者ユーザ名最小文字数 */
export const VISITOR_USERNAME_MIN_LENGTH = 1;

/** 来場者ユーザ名最大文字数 */
export const VISITOR_USERNAME_MAX_LENGTH = 20;

/** 既存来場者ログイン用ユーザ名最大文字数 */
export const VISITOR_LOGIN_USERNAME_MAX_LENGTH = 40;

/** 来場者パスワード最小文字数 */
export const VISITOR_PASSWORD_MIN_LENGTH = 4;

/** 来場者パスワード最大文字数 */
export const VISITOR_PASSWORD_MAX_LENGTH = 64;

/** 制御文字検出用正規表現 */
const CONTROL_CHARACTER_REGEX = /[\u0000-\u001f\u007f]/;

/** UUIDの正規表現 */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * 検証結果を生成する
 * @param {boolean} isValid - 妥当性
 * @param {string} [message=''] - 検証メッセージ
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const createValidationResult = (isValid, message = '') => {
  return { isValid, message };
};

/**
 * 空文字チェック
 * @param {string | undefined | null} value - チェック対象文字列
 * @returns {boolean} 空でない場合true
 */
export const validateNotEmpty = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  return String(value).trim().length > 0;
};

/**
 * ニックネーム文字列を正規化する
 * @param {string} nickname - ニックネーム
 * @returns {string} 正規化後ニックネーム
 */
export const normalizeNickname = (nickname) => {
  if (nickname === null || nickname === undefined) {
    return '';
  }

  return String(nickname).trim();
};

/**
 * ニックネームを検証する
 * @param {string} nickname - ニックネーム
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validateNickname = (nickname) => {
  if (!validateNotEmpty(nickname)) {
    return createValidationResult(false, 'ニックネームは必須です。');
  }

  /** 生のニックネーム */
  const rawNickname = String(nickname);

  if (CONTROL_CHARACTER_REGEX.test(rawNickname)) {
    return createValidationResult(false, 'ニックネームに制御文字は使用できません。');
  }

  /** 前後空白を除去したニックネーム */
  const trimmedNickname = normalizeNickname(rawNickname);

  if (trimmedNickname.length < NICKNAME_MIN_LENGTH) {
    return createValidationResult(false, 'ニックネームが短すぎます。');
  }

  if (trimmedNickname.length > NICKNAME_MAX_LENGTH) {
    return createValidationResult(
      false,
      `ニックネームは${NICKNAME_MAX_LENGTH}文字以内で入力してください。`,
    );
  }

  return createValidationResult(true);
};

/**
 * 来場者ユーザ名文字列を正規化する
 * @param {string} username - ユーザ名
 * @returns {string} 正規化後ユーザ名
 */
export const normalizeVisitorUsername = (username) => {
  if (username === null || username === undefined) {
    return '';
  }

  return String(username).trim();
};

/**
 * 来場者ユーザ名の文字数をUnicodeコードポイント単位で数える
 * @param {string} username - ユーザ名
 * @returns {number} 文字数
 */
export const countVisitorUsernameCharacters = (username) => {
  return Array.from(String(username ?? '')).length;
};

/**
 * 来場者ユーザ名を指定文字数以内へ切り詰める
 * @param {string} username - ユーザ名
 * @param {number} [maxLength=VISITOR_USERNAME_MAX_LENGTH] - 最大文字数
 * @returns {string} 切り詰め後ユーザ名
 */
export const truncateVisitorUsername = (
  username,
  maxLength = VISITOR_USERNAME_MAX_LENGTH,
) => {
  return Array.from(String(username ?? '')).slice(0, maxLength).join('');
};

/**
 * 来場者ユーザ名を指定上限で検証する
 * @param {string} username - ユーザ名
 * @param {number} maxLength - 最大文字数
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
const validateVisitorUsernameWithMaxLength = (username, maxLength) => {
  if (!validateNotEmpty(username)) {
    return createValidationResult(false, 'ユーザ名は必須です。');
  }

  /** 生のユーザ名 */
  const rawUsername = String(username);

  if (CONTROL_CHARACTER_REGEX.test(rawUsername)) {
    return createValidationResult(false, 'ユーザ名に制御文字は使用できません。');
  }

  /** 前後空白を除去したユーザ名 */
  const trimmedUsername = normalizeVisitorUsername(rawUsername);

  if (trimmedUsername.length < VISITOR_USERNAME_MIN_LENGTH) {
    return createValidationResult(false, 'ユーザ名が短すぎます。');
  }

  if (countVisitorUsernameCharacters(trimmedUsername) > maxLength) {
    return createValidationResult(
      false,
      `ユーザ名は${maxLength}文字以内で入力してください。`,
    );
  }

  return createValidationResult(true);
};

/**
 * 新規登録する来場者ユーザ名を検証する
 * @param {string} username - ユーザ名
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validateVisitorUsername = (username) => {
  return validateVisitorUsernameWithMaxLength(
    username,
    VISITOR_USERNAME_MAX_LENGTH,
  );
};

/**
 * 既存来場者のログイン用ユーザ名を検証する
 * @param {string} username - ユーザ名
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validateVisitorLoginUsername = (username) => {
  return validateVisitorUsernameWithMaxLength(
    username,
    VISITOR_LOGIN_USERNAME_MAX_LENGTH,
  );
};

/**
 * 来場者パスワードを検証する
 * @param {string} password - パスワード
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validateVisitorPassword = (password) => {
  if (!validateNotEmpty(password)) {
    return createValidationResult(false, 'パスワードは必須です。');
  }

  /** 生のパスワード */
  const rawPassword = String(password);

  if (CONTROL_CHARACTER_REGEX.test(rawPassword)) {
    return createValidationResult(false, 'パスワードに制御文字は使用できません。');
  }

  if (rawPassword.length < VISITOR_PASSWORD_MIN_LENGTH) {
    return createValidationResult(
      false,
      `パスワードは${VISITOR_PASSWORD_MIN_LENGTH}文字以上で入力してください。`,
    );
  }

  if (rawPassword.length > VISITOR_PASSWORD_MAX_LENGTH) {
    return createValidationResult(
      false,
      `パスワードは${VISITOR_PASSWORD_MAX_LENGTH}文字以内で入力してください。`,
    );
  }

  return createValidationResult(true);
};

/**
 * UUID形式を検証する
 * @param {string} value - UUID候補文字列
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validateUuid = (value) => {
  if (!validateNotEmpty(value)) {
    return createValidationResult(false, 'IDが指定されていません。');
  }

  if (!UUID_REGEX.test(value)) {
    return createValidationResult(false, 'IDの形式が不正です。');
  }

  return createValidationResult(true);
};

/**
 * ポイント加減算値を検証する
 * @param {number} pointsDelta - 加減算値
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validatePointsDelta = (pointsDelta) => {
  if (!Number.isInteger(pointsDelta)) {
    return createValidationResult(false, 'ポイント増減値は整数で指定してください。');
  }

  if (pointsDelta === 0) {
    return createValidationResult(false, 'ポイント増減値は0以外で指定してください。');
  }

  return createValidationResult(true);
};

/**
 * 必須文字列を検証する
 * @param {string} value - 検証対象
 * @param {string} fieldLabel - 表示用項目名
 * @returns {{ isValid: boolean, message: string }} 検証結果
 */
export const validateRequiredString = (value, fieldLabel) => {
  if (!validateNotEmpty(value)) {
    return createValidationResult(false, `${fieldLabel}は必須です。`);
  }

  return createValidationResult(true);
};
